import { el, ensureStyle } from '../dom';
import { icon } from '../components/icons';
import type { PbSession } from '../../core/storage/pb-auth';
import { t } from '../../strings';

ensureStyle(
  'sign-in-css',
  `
  .signin { flex:1; display:flex; flex-direction:column; justify-content:center;
    gap:22px; padding:32px var(--pad); }
  .signin .brand { text-align:center; display:flex; flex-direction:column;
    align-items:center; gap:12px; }
  .signin h1 { font-size:46px; margin:0; line-height:.95; }
  .signin .mark { color:var(--moss); }
  .signin .tag { display:block; }
  .signin form { display:flex; flex-direction:column; gap:12px; }
  .signin form > div { display:flex; flex-direction:column; gap:6px; }
  .signin button { width:100%; padding:15px; font-size:17px; }
  .signin .err { font-size:12.5px; color:var(--danger); line-height:1.5; min-height:1em; }
  .signin .note { text-align:center; }
`,
);

/**
 * Full-screen sign-in, shown before the app boots when the PocketBase backend
 * is configured. Resolves once a session exists; there is no self-signup —
 * accounts are created in the PocketBase dashboard (see docs/DEPLOY.md).
 */
export function signIn(root: HTMLElement, session: PbSession): Promise<void> {
  return new Promise((resolve) => {
    const email = el('input.input', {
      type: 'email',
      name: 'email',
      autocomplete: 'username',
      required: true,
    }) as HTMLInputElement;
    const password = el('input.input', {
      type: 'password',
      name: 'password',
      autocomplete: 'current-password',
      required: true,
    }) as HTMLInputElement;
    const submit = el(
      'button.btn.btn-primary',
      { type: 'submit' },
      t('app.signIn.submit'),
    ) as HTMLButtonElement;
    const error = el('div.err', {});

    const form = el(
      'form',
      {},
      el('div', {}, el('label.label', {}, t('app.signIn.email')), email),
      el('div', {}, el('label.label', {}, t('app.signIn.password')), password),
      submit,
      error,
    ) as HTMLFormElement;

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      submit.disabled = true;
      submit.textContent = t('app.signIn.submitting');
      error.textContent = '';
      try {
        await session.login(email.value.trim(), password.value);
        resolve();
      } catch (err) {
        // A wrong password and an unreachable server look the same from here,
        // so name the more likely cause when the server isn't answering.
        error.textContent = (await session.healthCheck())
          ? (err as Error).message
          : t('app.signIn.unreachable', { url: session.baseUrl || location.origin });
        submit.disabled = false;
        submit.textContent = t('app.signIn.submit');
        password.select();
      }
    });

    root.replaceChildren(
      el(
        'div.signin',
        {},
        el(
          'div.brand',
          {},
          icon('feather', { size: 40, class: 'mark' }),
          el('h1', {}, t('app.meta.name')),
          el('div.tag.label', {}, t('app.signIn.tagline')),
        ),
        form,
        el('div.note', {}, t('app.signIn.note')),
      ),
    );
    email.focus();
  });
}
